/**
 * Workspace-scope slice.
 *
 * Owns workspace scoping + sessionStorage save/restore (setScopeWorkspace),
 * product/tab/platform/brand selection, pending confirmation, transient
 * clearing, and the performance-forecast + last-image-ad-form fields.
 */

import type { StoreApi } from "zustand";
import type { AdsWorkspaceState } from "../useAdsWorkspaceStore.types";
import {
  clearFlatPersistedState,
  getFlatStateWorkspace,
  persistGeneratedAds,
  persistPendingJobs,
  persistSelectedBrandId,
  persistSubAgentState,
  restoreWorkspaceCompanionState,
  saveWorkspaceCompanionState,
  setFlatStateWorkspace,
} from "../adsWorkspacePersistence";

type SetState = StoreApi<AdsWorkspaceState>["setState"];
type GetState = StoreApi<AdsWorkspaceState>["getState"];

export type WorkspaceScopeActions = Pick<
  AdsWorkspaceState,
  | "setScopeWorkspace"
  | "setProducts"
  | "setSelectedProductIds"
  | "setActiveTab"
  | "setSelectedPlatform"
  | "setSelectedBrandId"
  | "setSelectedBrandConfig"
  | "setPendingConfirmation"
  | "clearTransient"
  | "setPerformanceForecast"
  | "setPerformanceForecasting"
  | "setLastImageAdForm"
>;

export function createWorkspaceScopeSlice(
  set: SetState,
  _get: GetState,
): WorkspaceScopeActions {
  return {
    setScopeWorkspace: (workspaceId) =>
      set((state) => {
        if (state.scopeWorkspaceId === workspaceId) return state;

        const isInitialCall = state.scopeWorkspaceId === null;

        // Save current workspace's companion state so it can be restored later
        if (state.scopeWorkspaceId) {
          saveWorkspaceCompanionState(state.scopeWorkspaceId, state);
        }

        // Try to restore the target workspace's saved companion state
        const restored = workspaceId ? restoreWorkspaceCompanionState(workspaceId) : null;

        // On the first call after page load, the store was already hydrated from
        // flat sessionStorage keys (which survive refresh within the tab). Reuse
        // that hydrated state ONLY when the flat keys are tagged as belonging to
        // this workspace — otherwise we'd silently adopt the previous session's
        // (or previous user's) ads into a brand-new workspace.
        const flatStateOwner = getFlatStateWorkspace();
        const flatStateMatchesWorkspace = Boolean(workspaceId) && flatStateOwner === workspaceId;
        const hydrated =
          isInitialCall && !restored && workspaceId && flatStateMatchesWorkspace
            ? {
                revealedSubAgents: state.revealedSubAgents,
                activeSubAgent: state.activeSubAgent,
                pendingBananJobs: state.pendingBananJobs,
                pendingKlingJobs: state.pendingKlingJobs,
                generatedAds: state.generatedAds,
                subAgentChats: state.subAgentChats,
                selectedBrandId: state.selectedBrandId,
              }
            : null;
        const effective = restored ?? hydrated;

        // Update flat sessionStorage keys to match effective/fresh state
        if (effective) {
          persistSubAgentState(
            effective.revealedSubAgents,
            effective.subAgentChats,
            effective.activeSubAgent,
          );
          persistPendingJobs(effective.pendingBananJobs, effective.pendingKlingJobs);
          persistGeneratedAds(effective.generatedAds);
          persistSelectedBrandId(effective.selectedBrandId ?? null);
          setFlatStateWorkspace(workspaceId ?? null);
          // Seed the per-workspace snapshot so subsequent switches round-trip.
          if (workspaceId && !restored) {
            saveWorkspaceCompanionState(workspaceId, { ...state, ...effective });
          }
        } else {
          clearFlatPersistedState();
          setFlatStateWorkspace(workspaceId ?? null);
        }

        return {
          scopeWorkspaceId: workspaceId,
          performanceForecast: null,
          performanceForecastProductId: null,
          performanceForecasting: false,
          companionSessionControls: null,
          selectedAssetIds: [],
          favoriteAssetIds: [],
          adPlatformConfig: { google: { connected: false }, meta: { connected: false } },
          selectedPlatform: state.selectedPlatform,
          updatedAt: Date.now(),
          // Companion state: restored from previous visit, preserved across
          // refresh via flat sessionStorage hydration, or fresh.
          generatedAds: effective?.generatedAds ?? [],
          pendingKlingJobs: effective?.pendingKlingJobs ?? [],
          pendingBananJobs: effective?.pendingBananJobs ?? [],
          activeSubAgent: effective?.activeSubAgent ?? null,
          revealedSubAgents: effective?.revealedSubAgents ?? [],
          subAgentChats: effective?.subAgentChats ?? {
            banan: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            vid: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            scout: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            gads: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            meta_ads: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            higgsfield: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            higgsfield_cli: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            agent_v: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
            moodboard: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
          },
          // Reset session-list state — pills belong to the previous workspace.
          subAgentSessionList: {
            banan: [],
            vid: [],
            scout: [],
            gads: [],
            meta_ads: [],
            higgsfield: [],
            higgsfield_cli: [],
            agent_v: [],
            moodboard: [],
          },
          // Transient busy flags — clear on workspace switch.
          subAgentBusy: {
            banan: false,
            vid: false,
            scout: false,
            gads: false,
            meta_ads: false,
            higgsfield: false,
            higgsfield_cli: false,
            agent_v: false,
            moodboard: false,
          },
          // Pending Scout research run is workspace-scoped — drop it on switch.
          pendingScoutResearchRun: null,
          pendingMarcusGuidedFlow:
            state.pendingMarcusGuidedFlow &&
            (!workspaceId ||
              !state.pendingMarcusGuidedFlow.workspaceId ||
              state.pendingMarcusGuidedFlow.workspaceId === workspaceId)
              ? state.pendingMarcusGuidedFlow
              : null,
          // Outcome filter signal is transient UI state — drop on switch.
          pendingMetaTasksOutcome: null,
          pendingGadsTasksOutcome: null,
          // Brand selection: restored from per-workspace snapshot, preserved
          // across refresh via flat sessionStorage hydration, or null.
          // selectedBrandConfig is fetched lazily by the AdsWorkspace effect.
          selectedBrandId: effective?.selectedBrandId ?? null,
          selectedBrandConfig: null,
        };
      }),

    setProducts: (products) => set({ products }),

    setSelectedProductIds: (ids) => set({ selectedProductIds: ids }),

    setActiveTab: (tab) => set({ activeTab: tab }),

    setSelectedPlatform: (platform) => set({ selectedPlatform: platform }),

    setSelectedBrandId: (brandId) =>
      set((state) => {
        // Clearing the cached config is intentional — AdsWorkspace's effect
        // re-fetches whenever selectedBrandId changes and re-populates
        // selectedBrandConfig. Avoids stale config leaking into context
        // snapshots between change → fetch.
        if (state.selectedBrandId === brandId) return state;
        return { selectedBrandId: brandId, selectedBrandConfig: null };
      }),

    setSelectedBrandConfig: (config) => set({ selectedBrandConfig: config }),

    setPendingConfirmation: (confirmation) => set({ pendingConfirmation: confirmation }),

    clearTransient: () =>
      set({
        pendingConfirmation: null,
        pendingMarcusGuidedFlow: null,
      }),

    setPerformanceForecast: (forecast, productId) =>
      set({
        performanceForecast: forecast,
        performanceForecastProductId: productId ?? null,
        performanceForecasting: false,
      }),

    setPerformanceForecasting: (v) => set({ performanceForecasting: v }),

    setLastImageAdForm: (form) => set({ lastImageAdForm: form }),
  };
}
